import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  Preview as PreviewIcon,
  Schedule as ScheduleIcon,
  Groups as GroupsIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import whatsappService, { 
  SpopInfo, 
  BroadcastCustomer, 
  BroadcastRequest 
} from '../services/whatsappService';

/** Format Date ke value datetime-local (waktu lokal, bukan UTC) */
const toLocalDatetimeValue = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** Parse value datetime-local menjadi Date lokal */
const fromLocalDatetimeValue = (value: string): Date | null => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const WhatsappBroadcast: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Step 1: SPOP Selection
  const [availableSpops, setAvailableSpops] = useState<SpopInfo[]>([]);
  const [selectedSpops, setSelectedSpops] = useState<string[]>([]);
  const [customerStatus, setCustomerStatus] = useState<string>('all');

  // Step 2: Message
  const [message, setMessage] = useState('');

  // Step 3: Schedule
  const [sendImmediately, setSendImmediately] = useState(true);
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);
  const [priority, setPriority] = useState(3);

  // Preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewCustomers, setPreviewCustomers] = useState<BroadcastCustomer[]>([]);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadAvailableSpops = useCallback(async (status: string) => {
    try {
      setLoading(true);
      const spops = await whatsappService.getAvailableSpops(status);
      setAvailableSpops(spops);
      // Hapus SPOP yang sudah tidak muncul setelah filter status berubah
      setSelectedSpops((prev) => prev.filter((id) => spops.some((s) => s.spop_id === id)));
    } catch (err) {
      console.error('Error loading SPOPs:', err);
      setError('Gagal memuat daftar SPOP');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAvailableSpops(customerStatus);
  }, [customerStatus, loadAvailableSpops]);

  const handleSpopToggle = (spopId: string) => {
    setSelectedSpops(prev => 
      prev.includes(spopId) 
        ? prev.filter(id => id !== spopId)
        : [...prev, spopId]
    );
  };

  const handleSelectAllSpops = () => {
    if (selectedSpops.length === availableSpops.length) {
      setSelectedSpops([]);
    } else {
      setSelectedSpops(availableSpops.map(spop => spop.spop_id));
    }
  };

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('message-textarea') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newMessage = message.slice(0, start) + variable + message.slice(end);
      setMessage(newMessage);
      
      // Set cursor position after the inserted variable
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + variable.length;
        textarea.focus();
      }, 0);
    }
  };

  const previewRecipients = async () => {
    if (selectedSpops.length === 0) {
      setError('Pilih minimal satu SPOP');
      return;
    }

    try {
      setPreviewLoading(true);
      setError(null);
      const response = await whatsappService.getCustomersBySpop(
        selectedSpops[0],
        1,
        50,
        customerStatus
      );
      setPreviewCustomers(response.customers);

      const total = availableSpops
        .filter((spop) => selectedSpops.includes(spop.spop_id))
        .reduce((sum, spop) => sum + spop.customer_count, 0);
      setPreviewTotal(total);

      setPreviewOpen(true);
    } catch (err) {
      console.error('Error previewing recipients:', err);
      setError('Gagal memuat preview penerima');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && selectedSpops.length === 0) {
      setError('Pilih minimal satu SPOP');
      return;
    }
    if (activeStep === 1 && !message.trim()) {
      setError('Pesan tidak boleh kosong');
      return;
    }
    if (activeStep === 2 && !sendImmediately && !scheduledAt) {
      setError('Pilih waktu jadwal pengiriman');
      return;
    }

    setError(null);
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSendBroadcast = async () => {
    try {
      setLoading(true);
      setError(null);

      const request: BroadcastRequest = {
        spop_ids: selectedSpops,
        message,
        customer_status: customerStatus === 'all' ? undefined : customerStatus,
        priority,
        send_immediately: sendImmediately,
        scheduled_at: scheduledAt ? scheduledAt.toISOString() : undefined
      };

      const result = await whatsappService.sendBroadcast(request);
      setSuccess(result.message);
      
      // Reset form
      setActiveStep(0);
      setSelectedSpops([]);
      setMessage('');
      setSendImmediately(true);
      setScheduledAt(null);
      setPriority(3);
      
    } catch (error: any) {
      console.error('Error sending broadcast:', error);
      setError(error.response?.data?.message || 'Gagal mengirim broadcast');
    } finally {
      setLoading(false);
    }
  };

  const getTotalRecipients = () => {
    return availableSpops
      .filter(spop => selectedSpops.includes(spop.spop_id))
      .reduce((sum, spop) => sum + spop.customer_count, 0);
  };

  return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          WhatsApp Broadcast
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Stepper activeStep={activeStep} orientation="vertical">
              {/* Step 1: SPOP Selection */}
              <Step>
                <StepLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupsIcon />
                    Pilih SPOP Target
                  </Box>
                </StepLabel>
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <FormControl fullWidth>
                          <InputLabel>Status Customer</InputLabel>
                          <Select
                            value={customerStatus}
                            onChange={(e) => setCustomerStatus(e.target.value)}
                            label="Status Customer"
                          >
                            <MenuItem value="all">Semua Status</MenuItem>
                            <MenuItem value="active">Aktif</MenuItem>
                            <MenuItem value="inactive">Tidak Aktif</MenuItem>
                            <MenuItem value="suspended">Suspended</MenuItem>
                            <MenuItem value="free">Free</MenuItem>
                            <MenuItem value="NonInternet">Non Internet</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Button
                          variant="outlined"
                          onClick={handleSelectAllSpops}
                          fullWidth
                        >
                          {selectedSpops.length === availableSpops.length ? 'Batal Pilih Semua' : 'Pilih Semua SPOP'}
                        </Button>
                      </Box>
                    </Box>

                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {availableSpops.map((spop) => (
                          <Box key={spop.spop_id} sx={{ minWidth: { xs: '100%', sm: '48%', md: '32%', lg: '24%' } }}>
                            <Card 
                              variant="outlined" 
                              sx={{ 
                                cursor: 'pointer',
                                border: selectedSpops.includes(spop.spop_id) ? 2 : 1,
                                borderColor: selectedSpops.includes(spop.spop_id) ? 'primary.main' : 'divider'
                              }}
                              onClick={() => handleSpopToggle(spop.spop_id)}
                            >
                              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={selectedSpops.includes(spop.spop_id)}
                                      onChange={() => handleSpopToggle(spop.spop_id)}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  }
                                  label={
                                    <Box>
                                      <Typography variant="subtitle2">
                                        {spop.spop_name}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {spop.spop_area_code}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {spop.customer_count} customer
                                      </Typography>
                                    </Box>
                                  }
                                />
                              </CardContent>
                            </Card>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {selectedSpops.length > 0 && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Total penerima: {getTotalRecipients()} customer dari {selectedSpops.length} SPOP
                      </Alert>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={selectedSpops.length === 0}
                    >
                      Lanjut
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={previewRecipients}
                      disabled={selectedSpops.length === 0 || previewLoading}
                      startIcon={previewLoading ? <CircularProgress size={20} /> : <PreviewIcon />}
                    >
                      Preview Penerima
                    </Button>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 2: Message */}
              <Step>
                <StepLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MessageIcon />
                    Tulis Pesan
                  </Box>
                </StepLabel>
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Variabel yang tersedia:
                    </Typography>
                    <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
                      Klik variabel untuk memasukkan ke pesan. Variabel akan diganti dengan data customer saat pengiriman.
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {[
                        { var: '{NAMA}', desc: 'Nama customer' },
                        { var: '{NO_WHATSAPP}', desc: 'Nomor WhatsApp' },
                        { var: '{STATUS}', desc: 'Status customer' },
                        { var: '{SPOP_ID}', desc: 'Nama SPOP' },
                        { var: '{TANGGAL}', desc: 'Tanggal hari ini' },
                        { var: '{WAKTU}', desc: 'Waktu saat ini' }
                      ].map((item) => (
                        <Tooltip key={item.var} title={item.desc} arrow>
                          <Chip
                            label={item.var}
                            onClick={() => insertVariable(item.var)}
                            size="small"
                            clickable
                            color="primary"
                            variant="outlined"
                          />
                        </Tooltip>
                      ))}
                    </Box>

                    <TextField
                      id="message-textarea"
                      fullWidth
                      multiline
                      rows={6}
                      label="Pesan Broadcast"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Ketik pesan Anda di sini. Gunakan variabel di atas untuk personalisasi."
                      helperText={`${message.length}/4096 karakter`}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" onClick={handleNext} disabled={!message.trim()}>
                      Lanjut
                    </Button>
                    <Button onClick={handleBack}>
                      Kembali
                    </Button>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 3: Schedule */}
              <Step>
                <StepLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon />
                    Jadwal Pengiriman
                  </Box>
                </StepLabel>
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={sendImmediately}
                          onChange={(e) => setSendImmediately(e.target.checked)}
                        />
                      }
                      label="Kirim sekarang"
                    />

                    {!sendImmediately && (
                      <Box sx={{ mt: 2 }}>
                        <TextField
                          label="Jadwal Pengiriman"
                          type="datetime-local"
                          value={scheduledAt ? toLocalDatetimeValue(scheduledAt) : ''}
                          onChange={(e) => setScheduledAt(fromLocalDatetimeValue(e.target.value))}
                          fullWidth
                          InputLabelProps={{
                            shrink: true,
                          }}
                          inputProps={{
                            min: toLocalDatetimeValue(new Date()),
                          }}
                        />
                      </Box>
                    )}

                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <InputLabel>Prioritas</InputLabel>
                      <Select
                        value={priority}
                        onChange={(e) => setPriority(Number(e.target.value))}
                        label="Prioritas"
                      >
                        <MenuItem value={1}>Sangat Tinggi</MenuItem>
                        <MenuItem value={2}>Tinggi</MenuItem>
                        <MenuItem value={3}>Normal</MenuItem>
                        <MenuItem value={4}>Rendah</MenuItem>
                        <MenuItem value={5}>Sangat Rendah</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" onClick={handleNext}>
                      Lanjut
                    </Button>
                    <Button onClick={handleBack}>
                      Kembali
                    </Button>
                  </Box>
                </StepContent>
              </Step>

              {/* Step 4: Confirmation */}
              <Step>
                <StepLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SendIcon />
                    Konfirmasi & Kirim
                  </Box>
                </StepLabel>
                <StepContent>
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Ringkasan Broadcast
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2">Target SPOP:</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {selectedSpops.map((spopId) => {
                                const spop = availableSpops.find(s => s.spop_id === spopId);
                                return (
                                  <Chip 
                                    key={spopId} 
                                    label={spop ? `${spop.spop_name} (${spop.spop_area_code})` : `SPOP ${spopId}`} 
                                    size="small" 
                                  />
                                );
                              })}
                            </Box>
                          </Box>
                          
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2">Total Penerima:</Typography>
                            <Typography variant="body1" color="primary" fontWeight="bold">
                              {getTotalRecipients()} customer
                            </Typography>
                          </Box>
                        </Box>

                        <Box>
                          <Typography variant="subtitle2">Pesan:</Typography>
                          <Paper variant="outlined" sx={{ p: 2, mt: 0.5, bgcolor: 'grey.50' }}>
                            <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                              {message}
                            </Typography>
                          </Paper>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2">Status Customer:</Typography>
                            <Typography variant="body2">
                              {customerStatus === 'all' ? 'Semua Status' : customerStatus}
                            </Typography>
                          </Box>

                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2">Pengiriman:</Typography>
                            <Typography variant="body2">
                              {sendImmediately 
                                ? 'Segera' 
                                : `Dijadwalkan: ${scheduledAt?.toLocaleString('id-ID')}`
                              }
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleSendBroadcast}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                      color="primary"
                    >
                      {loading ? 'Mengirim...' : 'Kirim Broadcast'}
                    </Button>
                    <Button onClick={handleBack} disabled={loading}>
                      Kembali
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            </Stepper>
          </CardContent>
        </Card>

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Preview Penerima ({previewTotal} total customer)
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Menampilkan sample dari {(() => {
                const spop = availableSpops.find(s => s.spop_id === selectedSpops[0]);
                return spop ? `${spop.spop_name} (${spop.spop_area_code})` : `SPOP ${selectedSpops[0]}`;
              })()} (maksimal 50 customer)
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nama</TableCell>
                    <TableCell>WhatsApp</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>SPOP</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewCustomers.map((customer) => (
                    <TableRow key={customer.id}>
                      <TableCell>{customer.nama_customer}</TableCell>
                      <TableCell>{customer.no_whatsapp}</TableCell>
                      <TableCell>
                        <Chip 
                          label={customer.status} 
                          size="small"
                          color={
                            customer.status === 'active' ? 'success' :
                            customer.status === 'suspended' ? 'warning' :
                            customer.status === 'inactive' ? 'error' :
                            customer.status === 'free' ? 'info' :
                            'default'
                          }
                        />
                      </TableCell>
                      <TableCell>{customer.spop_name || customer.spop_id}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewOpen(false)}>Tutup</Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default WhatsappBroadcast;
